import * as Sentry from "@sentry/react";

// Base URL for API endpoints
const BASE_URL = process.env.REACT_APP_BASE_URL;


// Functions to interact with the PDFMenu table

export async function getPdfById(restaurant, pdfId) {
    try {
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}/${pdfId}`);
        if (response.ok) {
            const pdf = await response.json();
            return pdf;
        } else {
            console.error('Failed to fetch PDF');
            return false;
        }
    } catch (error) {
        console.error('Error fetching PDF:', error);
        return false;
    }
}

export async function getActivePdf(restaurant) {
    
    const url = `${BASE_URL}/pdf/active/${restaurant}`;

    try {
        const response = await fetch(url);
        if (response.ok) {
            const pdfBlob = await response.blob();

            // Let op: blob() slaagt ook bij een lege body of een JSON-foutpagina.
            // createObjectURL geeft dan gewoon een geldige blob: URL terug en de
            // gebruiker krijgt een leeg bestand. Dit logt dat, maar verandert
            // het gedrag niet.
            const header = await pdfBlob.slice(0, 5).text().catch(() => "");

            if (pdfBlob.size === 0 || header !== "%PDF-") {
                const preview = await pdfBlob
                    .slice(0, 300)
                    .text()
                    .catch(() => "(binair)");

                Sentry.captureMessage("Active PDF: 200 OK maar geen PDF", {
                    level: "error",
                    tags: {
                        feature: "menu-pdf",
                        restaurant: String(restaurant),
                        function: "getActivePdf",
                        reason: pdfBlob.size === 0 ? "empty_body" : "not_a_pdf",
                    },
                    fingerprint: ["menu-pdf", "getActivePdf", "bad_body"],
                    extra: {
                        url,
                        status: response.status,
                        contentType: response.headers.get("content-type"),
                        contentLength: response.headers.get("content-length"),
                        blobSize: pdfBlob.size,
                        blobType: pdfBlob.type,
                        bodyPreview: preview,
                    },
                });

                // Wil je hier later een retry forceren, dan kun je `return null`
                // doen in plaats van de URL. Nu bewust ongewijzigd gelaten.
            }

            const objectUrl = window.URL.createObjectURL(pdfBlob); // Create a URL for the PDF blob
            return objectUrl; // Return the URL
        } else {
            const body = await response.text().catch(() => "(body niet leesbaar)");

            console.error("Failed to fetch active PDF: Response not OK");

            Sentry.captureMessage(`Active PDF: HTTP ${response.status}`, {
                level: "error",
                tags: {
                    feature: "menu-pdf",
                    restaurant: String(restaurant),
                    function: "getActivePdf",
                    reason: "http_error",
                    status: String(response.status),
                },
                fingerprint: ["menu-pdf", "getActivePdf", "http_error"],
                extra: {
                    url,
                    status: response.status,
                    statusText: response.statusText,
                    contentType: response.headers.get("content-type"),
                    bodyPreview: body.slice(0, 500),
                },
            });

            return null;
        }
    } catch (error) {
        console.error("Error fetching active PDF:", error);

        // Netwerkfout, CORS, DNS of adblocker. Dit is de categorie die je
        // backend per definitie nooit te zien krijgt.
        Sentry.captureException(error, {
            level: "error",
            tags: {
                feature: "menu-pdf",
                restaurant: String(restaurant),
                function: "getActivePdf",
                reason: "network_error",
                browserOnline: String(navigator.onLine),
            },
            fingerprint: ["menu-pdf", "getActivePdf", "network_error"],
            extra: { url },
        });

        return null; // Return null on error
    }
}

export async function getAllPdfs(restaurant) {
    try {
        console.log("Sending request...");
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}`);
        if (response.ok) {
            console.log("Response ok", response);
            const pdfs = await response.json();
            console.log("Pdfs extracted");
            return pdfs;
        } else {
            console.log("Response error");
            console.error('Failed to fetch PDFs');
            return false;
        }
    } catch (error) {
        console.log("Error fetching data...");
        console.error('Error fetching PDFs:', error);
        return false;
    }
}

export async function getAllPdfLogs(restaurant) {
    try {
        const response = await fetch(`${BASE_URL}/pdf/logs/${restaurant}`);
        if (response.ok) {
            console.log("Response ok", response);
            const pdfLogs = await response.json();
            console.log("Pdf logs extracted");
            return pdfLogs;
        } else {
            console.log("Response error");
            console.error('Failed to fetch PDF Logs');
            return false;
        }
    } catch (error) {
        console.log("Error fetching data...");
        console.error('Error fetching PDF Logs:', error);
        return false;
    }
}

export async function addPdf(restaurant, formData) {
    try {
        console.log("Passed restaurant: ", restaurant);
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}/upload`, {
            method: 'POST',
            body: formData,
        });

        if (response.ok) {
            const newPdf = await response.json();
            return newPdf;
        } else {
            console.error('Failed to add PDF');
            console.log(await response.json());
            return false;
        }
    } catch (error) {
        console.error('Error adding PDF:', error);
        return false;
    }
}

export async function updatePdf(restaurant, pdfId, data) {
    try {
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}/${pdfId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        if (response.ok) {
            return true;
        } else {
            console.error('Failed to update PDF');
            return false;
        }
    } catch (error) {
        console.error('Error updating PDF:', error);
        return false;
    }
}

export async function deletePdf(restaurant, pdfId) {
    try {
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}/${pdfId}`, {
            method: 'DELETE',
        });
        if (response.ok) {
            return true;
        } else {
            console.error('Failed to delete PDF');
            return false;
        }
    } catch (error) {
        console.error('Error deleting PDF:', error);
        return false;
    }
}

export async function setPdfActive(restaurant, pdfId) {
    try {
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}/${pdfId}/set_active`, {
            method: 'PUT',
        });
        if (response.ok) {
            return true;
        } else {
            console.error('Failed to update PDF active status');
            return false;
        }
    } catch (error) {
        console.error('Error updating PDF active status:', error);
        return false;
    }
}

export async function getAllPdfsMetadata(restaurant) {
    try {
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}/metadata`);
        if (response.ok) {
            const pdfs = await response.json();
            return pdfs;
        } else {
            console.error('Failed to fetch PDF metadata');
            return false;
        }
    } catch (error) {
        console.error('Error fetching PDF metadata:', error);
        return false;
    }
}

export async function getPdfData(restaurant, pdfId) {
    try {
        const response = await fetch(`${BASE_URL}/pdf/${restaurant}/${pdfId}/data`);
        if (response.ok) {
            const pdfData = await response.json();
            return pdfData;
        } else {
            console.error('Failed to fetch PDF data');
            return false;
        }
    } catch (error) {
        console.error('Error fetching PDF data:', error);
        return false;
    }
}
